import { createAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import drawConflictByType from './drawConflictByType'
import { RedrawShapeStoreState, selectRedrawState } from './handleUpdateOpeningGroupsSuccess'
import { drawOverlappingDrawableLocationsModifications } from './highlightOptionConflicts'
import { handleInsertingNewImageOverlay } from './insertOverlay'
import { markupDocument } from './markupDocument'
import { DocumentMapping } from '../../../models/documentMapping'
import { IMaterialModificationConflict } from '../../../models/masterSetPlan'
import { DRAWABLE_TYPES, DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Cutout, Overlay, Select, Workspace } from '../../lib/toolBoxes/2D'
import { determineIfMultiSelectable } from '../../lib/utils/functionality-bindings/addSelectFunctionalityToDrawable'
import addSelectFunctionalityToModifications from '../../lib/utils/functionality-bindings/addSelectFunctionalityToModifications'
import addSelectFunctionalityToModificationsToEdit from '../../lib/utils/functionality-bindings/addSelectFunctionalityToModificationsToEdit'
import {
    addOverlayChunkId,
    getCurrentDrawableLocationsFromRootState,
    resetEstimatedOptionOverlayChunkId,
    selectCurrentEstimatedOptionOverlayChunkIds,
    selectCurrentOverlayChunkIds,
    updateOverlayChunkId,
} from '../../slices/2D'
import { selectDocumentMappings } from '../../slices/documents'
import {
    selectSavedMaterialModifications,
    setConflictedMaterialOptionModifications,
    updateConflictResolutionFlag,
    updateOverlaidChunkLockedFlag,
    updateReviewingConflictsFlag,
} from '../../slices/masterSetPlan'
import { IMUP2DDrawableLocation, Vector2D } from '../../types'

export const adjustMaterialModificationsAction = createAction('AdjustMaterialModifications')

export function* drawModificationAndSendAreasToBack(
    materialConflict: IMaterialModificationConflict & {
        shapeType: DRAWING_TYPES
        opening_group_id: number
    },
    areaOpacity: number,
    lineOpacity: number,
    regionPaths: paper.Path[],
    workspaceTool: Workspace
) {
    const newItem: paper.Path | null = yield call(
        drawConflictByType,
        materialConflict,
        areaOpacity,
        lineOpacity,
        regionPaths,
        addSelectFunctionalityToModificationsToEdit
    )

    if (!newItem) return

    // if we draw the area we should send it to back just before the PlanRaster that other element will be on the top of it
    if (materialConflict.shapeType === DRAWING_TYPES.AREA) {
        yield call(workspaceTool.sendItemToBackBeforeRaster, newItem)
    }
}

export function* handleInsertingNewOverlay(
    currentEstimatedOptionOverlayChunkId: number,
    overlayIndex: number,
    overlayTool: Overlay,
    overlayMapping: DocumentMapping,
    resetOrMarkupFn: () => void
) {
    if (overlayIndex === 0) {
        // Use Reset action instead of the set to not
        // double trigger the actions we are going to call
        // below but not change the corresponding saga
        // insertEstimatedOptionOverlay.ts
        yield put(resetEstimatedOptionOverlayChunkId(null))

        yield call(overlayTool.resetOverlayRasters)
        yield call(overlayTool.resetSelectedImagesAndCrossHairs)
        yield call(overlayTool.lockOverlay, false)
        yield call(resetOrMarkupFn)
        yield put(updateOverlayChunkId(currentEstimatedOptionOverlayChunkId))
    } else {
        yield put(addOverlayChunkId(currentEstimatedOptionOverlayChunkId))
    }

    yield call(handleInsertingNewImageOverlay, {
        payload: currentEstimatedOptionOverlayChunkId,
        type: '2D/setOverlayChunkId',
    })

    yield call(overlayTool.updateOverlayScale, currentEstimatedOptionOverlayChunkId, overlayMapping.overlay_scale)

    yield call(overlayTool.rotateImageRaster, currentEstimatedOptionOverlayChunkId, overlayMapping.overlay_rotation)

    yield call(
        overlayTool.setOverlayPositionCoordinates,
        currentEstimatedOptionOverlayChunkId,
        overlayMapping.overlay_center as Vector2D
    )
}

export function* handleAdjustingMaterialModifications() {
    const paperManger: PaperManager | null = yield call(managers.get2DManager)

    if (!paperManger) return

    const workspaceTool: Workspace = yield call(paperManger.getTool, Workspace.NAME)
    const overlaidChunkIds: number[] = yield select(selectCurrentOverlayChunkIds)
    const drawableLocations: IMUP2DDrawableLocation[] = yield select(getCurrentDrawableLocationsFromRootState)
    const currentEstimatedOptionOverlayChunkIds: number[] = yield select(selectCurrentEstimatedOptionOverlayChunkIds)
    const documentMappings: DocumentMapping[] | null = yield select(selectDocumentMappings)
    const [overlayTool, cutoutTool]: [Overlay, Cutout] = yield call(paperManger.getTools, [Overlay.NAME, Cutout.NAME])

    if (!overlaidChunkIds.length && currentEstimatedOptionOverlayChunkIds.length) {
        if (!documentMappings) return

        for (let overlayIndex = 0; overlayIndex < currentEstimatedOptionOverlayChunkIds.length; overlayIndex++) {
            const currentEstimatedOptionOverlayChunkId = currentEstimatedOptionOverlayChunkIds[overlayIndex]
            const overlayMapping = documentMappings.find(
                (map) => map.document_chunk_id === currentEstimatedOptionOverlayChunkId
            )

            if (!overlayMapping || (overlayMapping && overlayMapping.overlay_center.length === 0)) return

            yield call(
                handleInsertingNewOverlay,
                currentEstimatedOptionOverlayChunkId,
                overlayIndex,
                overlayTool,
                overlayMapping,
                markupDocument
            )
        }

        // This is so that we fall into the other
        // condition of this function starting on line 72
        yield call(handleAdjustingMaterialModifications)
        yield call(paperManger.useTool, Select.NAME)
        yield put(updateOverlaidChunkLockedFlag(true))

        return
    }

    if (!overlaidChunkIds?.length) return

    const materialModifications: IMaterialModificationConflict[] = yield select(selectSavedMaterialModifications)
    const { areaOpacity, lineOpacity }: RedrawShapeStoreState = yield select(selectRedrawState)
    const overlayMappings = documentMappings?.filter((map) => overlaidChunkIds.includes(map.document_chunk_id))

    if (!overlayMappings || !overlayMappings.length) return

    const baseHomeMapping = documentMappings?.find(
        (map) => map.id === overlayMappings[0].additional_data.estimatedBaseHomeId
    )

    if (!baseHomeMapping) return

    const matMods = materialModifications.reduce((mods, initialMod) => {
        const drawableLocation = drawableLocations.find(
            (loc) => loc.opening_location_id === initialMod.material_location_id
        )

        if (!drawableLocation || !initialMod.option_ids.every((id) => overlaidChunkIds.includes(id))) return mods

        const isModificationDeletion = workspaceTool.compareCoordinatesUsingAPIModels(
            {
                coordinates: drawableLocation.coordinates,
                cutouts: drawableLocation.cutouts,
            },
            initialMod.coordinates,
            initialMod.cutouts || []
        )

        if (isModificationDeletion) return mods

        return [
            ...mods,
            {
                ...initialMod,
                shapeType: drawableLocation.shapeType ?? DRAWING_TYPES.SECTION,
                opening_group_id: drawableLocation.opening_group_id,
            },
        ]
    }, [] as (IMaterialModificationConflict & { shapeType: DRAWING_TYPES; opening_group_id: number })[])

    yield put(updateReviewingConflictsFlag(true))
    yield put(updateConflictResolutionFlag(false))

    const materialDrawn: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) => data.drawable_id || data.ai_suggestion_id
    )

    materialDrawn.map((mat) => (mat.visible = false))

    if (matMods.length > 0) {
        yield all(
            matMods.map((materialConflict) => {
                return call(
                    drawModificationAndSendAreasToBack,
                    materialConflict,
                    areaOpacity,
                    lineOpacity,
                    [] as paper.Path[],
                    workspaceTool
                )
            })
        )
    }

    const matModLocationIds = materialModifications
        .filter((initialMod) => initialMod.option_ids.every((id) => overlaidChunkIds.includes(id)))
        .map((matMod) => matMod.material_location_id)

    const overlappingDrawableLocationsThatAreNotModifications = drawableLocations.filter((location) => {
        const item = workspaceTool.getItemWithOpeningLocationId(location.opening_location_id)
        const isPossibleKingStud = location.drawing_type === DRAWABLE_TYPES.WALL

        return (
            item &&
            location.drawing_type !== DRAWABLE_TYPES.HIGHLIGHTER &&
            location.drawing_type !== DRAWABLE_TYPES.NOTE &&
            !matModLocationIds.includes(item.data.opening_location_id) &&
            // We want to be able to delete king studs
            // but those are not multiselectable
            (determineIfMultiSelectable(item) || isPossibleKingStud) &&
            overlaidChunkIds.some((id) => overlayTool.isPathInsideOverlay(item, id)) &&
            location.document_chunk_id === baseHomeMapping.document_chunk_id
        )
    })

    const newPotentialMaterialConflicts: (IMaterialModificationConflict & {
        shapeType: DRAWING_TYPES
        opening_group_id: number
    })[] = yield call(
        drawOverlappingDrawableLocationsModifications,
        overlappingDrawableLocationsThatAreNotModifications,
        overlaidChunkIds,
        areaOpacity,
        lineOpacity,
        [] as paper.Path[]
    )

    if (newPotentialMaterialConflicts.length > 0) {
        yield put(
            setConflictedMaterialOptionModifications(
                newPotentialMaterialConflicts.map((mod) => ({
                    ...mod,
                    shapeType: undefined,
                    opening_group_id: undefined,
                }))
            )
        )
    }

    yield call([cutoutTool, 'setItemMetadataUpdateFunction'], (item) => {
        if (item.data.isTemporary) {
            addSelectFunctionalityToModifications(item)
        } else {
            addSelectFunctionalityToModificationsToEdit(item)
        }
    })
}

export function* watchForAdjustingMaterialModifications() {
    yield takeEvery(adjustMaterialModificationsAction, handleAdjustingMaterialModifications)
}
